import { FC, useCallback, useMemo } from "react";
import { useLocation } from "react-router";

import Loading from "web/Layout/Common/Loading";
import LoadMore from "web/Layout/LoadMore";
import __ from "web/Layout/Translations";

import Title from "web/Components/Common/Title";
import withScrollToTop from "web/Components/Common/withScrollToTop/withScrollToTop";

import FiltersIcon from "web/assets/icons/filters.svg";

import { ModalActionTypes } from "web/hooks/useModal/useModalTypes";

import { ICodeItem } from "web/types/Code";

import { useAppContext } from "web/context/app";
import { FilterValues } from "web/features/codes/utils/helpers";

import List from "../Shared/List";
import Filters from "./Filters";
import classes from "./codeList.scss";

interface ICustomerCodeListMobileProps {
  className: string;
  codes: ICodeItem[];
  isLoading: boolean;
  toUseCount: number;
  usedCount: number;
  canceledCount: number;
  totalCount: number;
}

const titleClasses = {
  root: classes.header,
  header: classes.title,
};
const name = "availability";

const CustomerCodeListMobile: FC<ICustomerCodeListMobileProps> = ({
  className,
  codes,
  isLoading,
  toUseCount,
  usedCount,
  canceledCount,
  totalCount,
}) => {
  const { search } = useLocation();

  const params = new URLSearchParams(decodeURIComponent(search));
  const filters = useMemo(() => {
    return [
      {
        label: `${__("Wszystkie")} (${totalCount})`,
        value: FilterValues.ALL,
        count: totalCount,
      },
      {
        label: `${__("Niewykorzystane")} (${toUseCount})`,
        value: FilterValues.USE,
        count: toUseCount,
      },
      {
        label: `${__("Wykorzystane")} (${usedCount})`,
        value: FilterValues.USED,
        count: usedCount,
      },
      {
        label: `${__("Anulowane")} (${canceledCount})`,
        value: FilterValues.CANCELED,
        count: canceledCount,
      },
    ];
  }, [toUseCount, usedCount, canceledCount, totalCount]);
  const type = params.get(name) || FilterValues.ALL;

  const currentTotal = codes?.length;
  const filterTotalCount =
    filters.find((filter) => filter.value === type)?.count || 0;

  let title;
  let codesAmount;
  switch (type) {
    case "use": {
      title = __("Niewykorzystane");
      codesAmount = toUseCount;
      break;
    }
    case "used": {
      title = __("Wykorzystane");
      codesAmount = usedCount;
      break;
    }
    case "canceled": {
      title = __("Anulowane");
      codesAmount = canceledCount;
      break;
    }
    default: {
      title = __("Wszystkie");
      codesAmount = totalCount;
      break;
    }
  }

  const { modal } = useAppContext();
  const { dispatch } = modal;

  const closeModal = useCallback(() => {
    dispatch({ type: ModalActionTypes.RESET });
  }, [dispatch]);

  const setModalAction = useCallback(() => {
    dispatch({
      type: ModalActionTypes.ADD_MODAL,
      modal: <Filters name={name} filters={filters} closeModal={closeModal} />,
      ariaLabel: __("Filtry"),
    });
  }, [dispatch, Filters, filters]);

  return (
    <div className={className}>
      <div className={classes.titleContainer}>
        <Title
          name={title}
          count={codesAmount}
          classes={titleClasses}
          container="h2"
          dataT1="code_list_title"
          ariaLabelBadge={__("Liczba kodów")}
        />
        <button
          type="button"
          className={classes.filterButton}
          onClick={setModalAction}
          aria-label={__("Otwórz filtry")}
        >
          <FiltersIcon width={18} className={classes.filterIcon} />
        </button>
      </div>

      <List codes={codes} />
      {isLoading ? <Loading className={classes.loading} /> : null}
      {!!currentTotal && filterTotalCount > currentTotal && (
        <div className={classes.loadMoreContainer}>
          <LoadMore
            total={filterTotalCount}
            currentTotal={currentTotal}
            shouldChangePage
            loading={isLoading}
          />
        </div>
      )}
    </div>
  );
};

export default withScrollToTop(CustomerCodeListMobile);
